<template v-if="loaded">
  <div class="contentContainer grow is-position-relative">
    <span
      v-if="
        !required
        && kycSubStep
        && ![B2B_KYC_SUB_STEP.DATA_CHECK, B2B_FLOW_STEPS.INTRO].includes(kycSubStep)
        && ![KYC_STATUS.COMPLETED, KYC_STATUS.APPROVED].includes(kycStore.kyc.status)
      "
      class="link-button link-button-primary"
      style="
        position: absolute;
        right: 0;
        top: 0;
        margin-right: 16px;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
      "
      @click="isSkipPopupOpen = true"
    > 
      {{ $t("bnpl:kyc.skip") }}
      <i class="pi pi-arrow-right" style="font-size: 0.75rem" />
    </span>
    <IdentiffKycIntro v-if="kycSubStep === B2B_KYC_SUB_STEP.INTRO" />
    <IdentiffKycCode
      v-if="kycSubStep === B2B_KYC_SUB_STEP.CODE"
      :skippable="!introKyc"
      @skip="jumpToDataCheck"
    />
    <IdentiffKycWaiting v-if="kycSubStep === B2B_KYC_SUB_STEP.WAITING" />
    <IdentiffKycResult v-if="kycSubStep === B2B_KYC_SUB_STEP.RESULT" />
    <IdentiffKycDataCheck v-if="kycSubStep === B2B_KYC_SUB_STEP.DATA_CHECK" />
  </div>
  <ActionArea
    ref="actionArea"
    is-prev-link
    prev-icon="pi-arrow-left"
    next-icon="pi-arrow-right"
    @prev="previousStep"
    @next="nextStep"
  />
  <Popup v-if="isSkipPopupOpen" @close="isSkipPopupOpen = false">
    <IdentiffKycSkipPopup
      @skip="() => {
        isSkipPopupOpen = false
        jumpToDataCheck()
      }"
      @pay-now="() => {
        isSkipPopupOpen = false
        payNow()
      }"
    />
  </Popup>
</template>

<script setup>
import { B2B_KYC_SUB_STEP, KYC_STATUS, KYC_INTERNAL_STATUS, KYC_HOST_FLOW } from '~~/utils/const';
import { useI18n } from 'vue-i18n';
import { useKycStore } from '~~/stores/kyc';
import { useBnplApplicationStore } from '~~/stores/bnplapplication';
import { useCustomerLimitStore } from '~~/stores/customerLimit';
import { useCountryStore } from '~~/stores/country';
import { useCartStore } from '~~/stores/cart';
import { usePaymentStore } from '~~/stores/payment';

const props = defineProps({
  stepIndex: {
    type: Number,
  },
  required: {
    type: Boolean,
  },
  /**
   * The gate at the start of a new igénylés. There is no skip and no way back: the customer either
   * finishes the identification or the application is closed. Off by default so the optional
   * in-flow KYC keeps behaving as it always has.
   */
  introKyc: {
    type: Boolean,
    default: false,
  },
  /** Defaults to the route, which is where every application-shaped flow keeps it. */
  reference: {
    type: String,
    default: null,
  },
  hostFlow: {
    type: String,
    default: KYC_HOST_FLOW.BNPL_APPLICATION,
  },
})

const route = useRoute();
const i18n = useI18n();

const kycStore = useKycStore();
const bnplApplicationStore = useBnplApplicationStore()
const customerLimitStore = useCustomerLimitStore()
const countryStore = useCountryStore()
const cartStore = useCartStore()
const paymentStore = usePaymentStore()

const reference = computed(() => props.reference || route.params.publicId || route.params.prescoreId);

// Claim the store during setup, not in onMounted. The sub-step components are children, so their
// onMounted runs before ours and IdentiffKycCode would otherwise start an identification against
// the previous flow's reference; the immediate watchers below would likewise act on a KYC left
// behind by an earlier igénylés. Nothing carried over may decide anything about this one.
if (props.introKyc) {
  kycStore.resetKyc();
}
kycStore.hostFlow = props.hostFlow;
kycStore.reference = reference.value;
kycStore.introKyc = props.introKyc;

const loaded = ref(false);
const pollingInterval = ref(null);
const isPolling = computed(() => !!pollingInterval.value);

const kycSubStep = computed(() => {
  return kycStore.currentPosition;
});

useScrollTopOnChange(kycSubStep);

const actionArea = ref(null);

const isSkipPopupOpen = ref(false);
const isClosing = ref(false);

function startPolling(millis = 5000) {
  stopPolling();
  pollingInterval.value = setInterval(() => {
    kycStore.getMyKyc(reference.value);
  }, millis);
}

function stopPolling() {
  if (!!pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
}

// The intro KYC cannot be skipped - skipKyc marks it SKIPPED, which never satisfies the gate, so
// the customer would be parked on the data check with no way forward. `required` hides the skip
// link and `:skippable` hides the child's skip button, which is where that rule is enforced.
async function jumpToDataCheck() {
  await kycStore.skipKyc(reference.value);
  stopPolling();
  kycStore.currentPosition = B2B_KYC_SUB_STEP.DATA_CHECK;
}

async function payNow() {
  const paymentData = await paymentStore.startPayNow(route.params.publicId)
  // console.log(paymentData)
  window.open(paymentData.gatewayUrl, "_self");
}

watch(
  () => kycStore.kyc,
  ({status, progressStep, internalStatus}) => {
    // An intro KYC has nothing to reuse - the gate only fires when there is no valid one - and a
    // SKIPPED record would park the customer on the data check without ever satisfying the gate.
    if (!props.introKyc && (kycStore.activeKyc || internalStatus === "SKIPPED")) {
      kycStore.currentPosition = B2B_KYC_SUB_STEP.DATA_CHECK;
      return;
    }
    switch (status) {
      case KYC_STATUS.CREATED:
        startPolling();
        kycStore.currentPosition = B2B_KYC_SUB_STEP.CODE;
        // setCancelTimeout();
        break;
      case KYC_STATUS.IN_PROGRESS:
        // clearCancelTimeout();
        startPolling();
        kycStore.currentPosition = B2B_KYC_SUB_STEP.WAITING;
        break;
      case KYC_STATUS.APPROVED:
      case KYC_STATUS.COMPLETED:
        stopPolling();
        // clearCancelTimeout();
        kycStore.currentPosition = B2B_KYC_SUB_STEP.RESULT;
        break;
      case KYC_STATUS.MANUAL_REVIEW:
        // clearCancelTimeout();
        if (progressStep === "OUTRO") {
          startPolling(60000);
          kycStore.currentPosition = B2B_KYC_SUB_STEP.RESULT;
        } else {
          startPolling();
          kycStore.currentPosition = B2B_KYC_SUB_STEP.WAITING;
        }
        break;
      case KYC_STATUS.CANCELLED:
      case KYC_STATUS.REJECTED:
      case KYC_STATUS.TERMINATED:
        stopPolling();
        // clearCancelTimeout();
        kycStore.currentPosition = B2B_KYC_SUB_STEP.RESULT;
        break;
    }
  },
  { immediate: true, deep: true }
)

watch(
  kycSubStep,
  (newStep) => {
    if (newStep === B2B_KYC_SUB_STEP.CODE || newStep === B2B_KYC_SUB_STEP.WAITING) {
      if (!isPolling.value) {
        startPolling();
      }
    }
  }
)

watch(
  [() => kycStore.kyc.status, () => kycStore.kyc.internalStatus],
  ([status, internalStatus]) => {
    if (!props.introKyc) return;
    // CANCELLED is left out on purpose - the customer can walk away from the vendor screen and the
    // result step lets them start over. A rejection or a termination is final.
    if (
      internalStatus === KYC_INTERNAL_STATUS.REJECTED
      || [KYC_STATUS.REJECTED, KYC_STATUS.TERMINATED].includes(status)
    ) {
      closeAfterFailedKyc();
    }
  }
  // Deliberately not immediate: at setup the store may still hold the identification from an
  // earlier igénylés, and acting on that would close this one before it has even loaded.
)

watch(
  [
    kycSubStep,
    actionArea,
    () => kycStore.kyc.status,
    () => kycStore.kyc.internalStatus,
  ],
  ([kycSubStep, _, status, internalStatus]) => {
    if (!actionArea.value) return;
    actionArea.value.reset();
    switch (kycSubStep) {
      case B2B_KYC_SUB_STEP.INTRO:
        actionArea.value.isPrevHidden = true;
        break;
      case B2B_KYC_SUB_STEP.CODE:
      case B2B_KYC_SUB_STEP.WAITING:
        actionArea.value.isPrevHidden = true;
        actionArea.value.isNextHidden = true;
        break;
      case B2B_KYC_SUB_STEP.RESULT:
        switch (internalStatus) {
          case KYC_INTERNAL_STATUS.REJECTED:
            actionArea.value.isNextHidden = true;
            actionArea.value.isPrevHidden = true;
            break
          default:
            switch (status) {
              case KYC_STATUS.COMPLETED:
              case KYC_STATUS.APPROVED:
                actionArea.value.isPrevHidden = true
                break;
              case KYC_STATUS.CANCELLED:
              case KYC_STATUS.REJECTED:
              case KYC_STATUS.TERMINATED:
                actionArea.value.isPrevHidden = true;
                break;
            }
        }
        break;
      case B2B_KYC_SUB_STEP.DATA_CHECK:
        // actionArea.value.isPrevHidden = true;
        actionArea.value.prevIcon = "pi-undo";
        actionArea.value.prevLabel = i18n.t("bnpl:b2c_kyc.actiona_area.restart");
        break;
    }
  },
  { immediate: true }
)

async function nextStep() {
  switch (kycStore.currentPosition) {
    case B2B_KYC_SUB_STEP.INTRO:
      kycStore.nextStep();
      break;
    case B2B_KYC_SUB_STEP.CODE:
    case B2B_KYC_SUB_STEP.WAITING:
      break;
    case B2B_KYC_SUB_STEP.RESULT:
      switch (kycStore.kyc.internalStatus) {
        case KYC_INTERNAL_STATUS.REJECTED:
          break
        default:
          switch (kycStore.kyc.status) {
            case KYC_STATUS.COMPLETED:
            case KYC_STATUS.APPROVED:
              kycStore.nextStep();
              break;
            case KYC_STATUS.CANCELLED:
            case KYC_STATUS.REJECTED:
            case KYC_STATUS.TERMINATED:
              await kycStore.startIdentification(reference.value);
              kycStore.currentPosition = B2B_KYC_SUB_STEP.CODE;
              break;
          }
      }
      break;
    case B2B_KYC_SUB_STEP.DATA_CHECK:
      try {
        if (!props.required || kycStore.isCustomerDataFilled) {
          await completeDataCheck();
        } else {
          useToast().add({
            message: i18n.t("b2b:kyc.data_check.empty_fields_error"),
            duration: 5000,
            type: "error",
          })
        }
      } catch (error) {
        if (error.message == "409") {
          useToast().add({
            message: i18n.t("b2b:kyc.data_check.difference_too_big_error"),
            duration: 5000,
            type: "error",
          })
        }
      }
      break;
  }
}

/**
 * The KYC finishes by advancing whichever flow is hosting it. Only the bnpl application store knows
 * how to do that for an application; the customer limit walks its own stepper.
 */
async function completeDataCheck() {
  if (props.hostFlow === KYC_HOST_FLOW.CUSTOMER_LIMIT) {
    await kycStore.updateCustomer(false, kycStore.customerData);
    await customerLimitStore.nextStep(countryStore.countryId);
    await customerLimitStore.fetchIntroKyc(countryStore.countryId);
    return;
  }

  await kycStore.nextStep();
  // The gate has just been satisfied, so re-ask: for a customer in arrears this is what lets the
  // risky-user screen take over again.
  await bnplApplicationStore.fetchIntroKyc();
}

/**
 * An intro identification has no way out other than finishing it, so a rejected or terminated one
 * ends the igénylés rather than leaving the customer on a dead screen.
 */
async function closeAfterFailedKyc() {
  if (isClosing.value) return;
  isClosing.value = true;
  stopPolling();

  try {
    if (props.hostFlow === KYC_HOST_FLOW.CUSTOMER_LIMIT) {
      // The stepper reads the rejected identification and moves the limit to CLOSED.
      await customerLimitStore.nextStep(countryStore.countryId);
      return;
    }
    await bnplApplicationStore.close();
    window.open(cartStore.cart.redirectUrl || "/", "_self");
  } catch (error) {
    console.log("KYC:: could not close the igénylés after a failed intro KYC", error);
    isClosing.value = false;
  }
}

async function previousStep() {
  switch (kycStore.currentPosition) {
    case B2B_KYC_SUB_STEP.INTRO:
      // There is nothing behind an intro KYC - it is the first step of the igénylés.
      if (!props.introKyc && props.stepIndex > 0) {
        bnplApplicationStore.stepBack()
      }
      break
    case B2B_KYC_SUB_STEP.DATA_CHECK:
      if (props.hostFlow === KYC_HOST_FLOW.CUSTOMER_LIMIT) {
        // The restart endpoint is keyed on an application; the customer limit just starts a new one.
        kycStore.activeKyc = false;
        await kycStore.startIdentification(reference.value);
      } else {
        await kycStore.restartKyc(reference.value);
      }
      kycStore.currentPosition = B2B_KYC_SUB_STEP.CODE;
      break
    case B2B_KYC_SUB_STEP.CODE:
    case B2B_KYC_SUB_STEP.WAITING:
    case B2B_KYC_SUB_STEP.RESULT:
  }
}

onMounted(async () => {
  // An intro KYC never reuses an earlier one: the gate only fires when the customer has no valid
  // KYC, so asking would either find nothing or short-circuit past the identification we need.
  if (!props.introKyc) {
    try {
      await kycStore.getActiveKycByCustomer();
      loaded.value = true;
      return;
    } catch {
      // No customer-wide KYC to reuse - fall through and resume or start this flow's own.
    }
  }
  try {
    await kycStore.getMyKyc(reference.value);
  } catch {
    kycStore.currentPosition = B2B_KYC_SUB_STEP.INTRO;
  }
  loaded.value = true;
})

onBeforeUnmount(() => {
  stopPolling();
})
</script>

<style>

</style>